import { createFileRoute } from "@tanstack/react-router";
import { Code2, Plug, Puzzle } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/integrations-hero.webp";
import syncImg from "@/assets/integrations-sync.webp";
import apiImg from "@/assets/integrations-api.webp";

const IMG: Record<string, string> = { hero: heroImg, sync: syncImg, api: apiImg };

export const Route = createFileRoute("/el/platform/integrations")({
  head: () => ({
    meta: [
      { title: "Διασυνδέσεις και API — banana.cards" },
      { name: "description", content: "Αμφίδρομος συγχρονισμός με Google, Outlook, Slack, Notion, Sheets και 50+ εφαρμογές. Μαζί με εύχρηστο REST API και υπογεγραμμένα webhooks." },
      { property: "og:title", content: "Διασυνδέσεις και API — banana.cards" },
      { property: "og:description", content: "Αμφίδρομος συγχρονισμός με Google, Outlook, Slack, Notion, Sheets και 50+ εφαρμογές. Μαζί με εύχρηστο REST API και υπογεγραμμένα webhooks." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "el_GR" },
    ],
    links: alternateLinkList("/platform/integrations", "el"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Διασυνδέσεις"
      title="Συνεργάζεται άψογα με"
      italic="όλα σας τα εργαλεία."
      description="Ενσωματωμένες διασυνδέσεις με Google, Outlook, Slack, Notion, Sheets, Zapier, n8n και 50+ εργαλεία. Μαζί με REST API και webhooks για οτιδήποτε άλλο."
      heroImage={IMG['hero']}
      metrics={[{ v: "50+", l: "ενσωματωμένες εφαρμογές" }, { v: "5000+", l: "μέσω Zapier" }, { v: "REST", l: "και webhooks" }]}
      bullets={[
        { icon: Plug, title: "Ενσωματωμένες εφαρμογές", desc: "Αμφίδρομος συγχρονισμός με τα εργαλεία που ήδη χρησιμοποιεί η ομάδα σας." },
        { icon: Puzzle, title: "Zapier και n8n", desc: "Πρόσβαση σε 5.000+ εφαρμογές μέσω διασυνδέσεων Zapier και n8n." },
        { icon: Code2, title: "API και webhooks", desc: "Δημιουργήστε τις δικές σας λύσεις — πλήρες REST API και υπογεγραμμένα webhooks." }
      ]}
      deepDives={[
        { eyebrow: "Συγχρονισμός", title: "Αμφίδρομα,", italic: "χωρίς συγκρούσεις.", description: "Επαφές, συμφωνίες και ημερολόγια συγχρονίζονται αμφίδρομα, με επίλυση συγκρούσεων και αρχείο συγχρονισμού ανά εγγραφή.", image: IMG["sync"], reverse: false, points: ["Google Workspace και Microsoft 365", "HubSpot, Salesforce και Pipedrive", "Slack, Notion και Sheets", "Αρχείο συγχρονισμού ανά εγγραφή"] },
        { eyebrow: "API", title: "Ένα API που", italic: "θα αγαπήσουν οι developers σας.", description: "Εύχρηστα REST endpoints, προδιαγραφή OpenAPI, γενναιόδωρα όρια αιτημάτων και υπογεγραμμένα webhooks για κάθε συμβάν.", image: IMG["api"], reverse: true, points: ["Προδιαγραφή OpenAPI 3", "Υπογεγραμμένα HMAC webhooks", "OAuth και κλειδί API", "Όρια αιτημάτων και SDK"] }
      ]}
      howItWorks={[
        { title: "Επιλέξτε εφαρμογή", desc: "Αναζητήστε σε 50+ ενσωματωμένες διασυνδέσεις." },
        { title: "Συνδεθείτε με ασφάλεια", desc: "OAuth, tokens περιορισμένων δικαιωμάτων και καταγραφή ελέγχου." },
        { title: "Αντιστοιχίστε και συγχρονίστε", desc: "Αντιστοίχιση πεδίων με προεπισκόπηση." },
        { title: "Δημιουργήστε περισσότερα με το API", desc: "Webhooks και REST για οτιδήποτε άλλο." }
      ]}
      capabilities={["50+ integration native", "5.000+ εφαρμογές μέσω Zapier και n8n", "REST API με OpenAPI", "Υπογεγραμμένα HMAC webhooks", "OAuth και κλειδί API", "HubSpot, Salesforce και Pipedrive", "Όρια αιτημάτων ανά χώρο εργασίας", "Περιβάλλον δοκιμών"]}
      faq={[
        { q: "Ποια εργαλεία συνδέονται απευθείας με το Banana;", a: "Οι ενσωματωμένες διασυνδέσεις περιλαμβάνουν Zapier, Salesforce, HubSpot και Zoho CRM, καθώς και συγχρονισμό με Google, Microsoft και άλλες υπηρεσίες. Με τους συνδέσμους Zapier και n8n έχετε πρόσβαση σε χιλιάδες επιπλέον εφαρμογές." },
        { q: "Υπάρχει API;", a: "Ναι — REST API με προδιαγραφή OpenAPI 3, γενναιόδωρα όρια αιτημάτων, υπογεγραμμένα HMAC webhooks και περιβάλλον δοκιμών για τις δικές σας διασυνδέσεις." },
        { q: "Ο συγχρονισμός είναι αμφίδρομος;", a: "Ναι — επαφές, συμφωνίες και ημερολόγια συγχρονίζονται και προς τις δύο κατευθύνσεις, με επίλυση συγκρούσεων και αρχείο συγχρονισμού ανά εγγραφή." },
        { q: "Υπάρχουν όρια αιτημάτων;", a: "Ναι — το REST API διαθέτει όρια αιτημάτων ανά χώρο εργασίας, ώστε οι διασυνδέσεις να λειτουργούν αξιόπιστα." }
      ]}
      outro={{ title: "Συνδέστε τα πάντα.", italic: "Δημιουργήστε οτιδήποτε.", description: "Ενσωματωμένες διασυνδέσεις, ένα API που θα αγαπήσει η ομάδα σας και webhooks για οτιδήποτε άλλο." }}
    />
  );
}